import { Component, Input, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { interval, Subject } from 'rxjs';
import { takeUntil, first, switchMap } from 'rxjs/operators';

import { ClassesEntity } from '../../../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { UnifiedVmLauncherService } from '../../../../../../services/unified-vm-launcher.service';
import { ConfirmDialogService } from '../../../../../../services/confirm-dialog.service';
import { ResourceCreatorService } from '../../../../../../services/resource-creator.service';
import { ResourceMasterDataService } from '../../../../../../services/resource-master-data.service';
import { Log } from '../../../../../../services/logger.service';
import { UserVmProvisionState } from '../../../../../../services/entity-services/classes-entity.service';
import { TemplateInfoCardData } from '../../../../../shared/template-info-card/template-info-card.component';

/**
 * ACC credential provisioning states for the student detail tab.
 * Mirrors the backend UaProvisioningStatus with an additional `fetching` transient state.
 */
export type AccCredentialState =
  | null          // initial — not yet polled
  | 'fetching'    // first GET in flight
  | 'initiating'  // 404 "Provisioning initiated" — just fired, polling
  | 'creating'    // 404 "in progress" — backend is provisioning
  | 'active'      // 200 OK — credentials ready
  | 'failed';     // 404 "failed" — permanent error until admin retries

@Component({
  selector: 'app-detail-tab',
  templateUrl: './detail-tab.component.html',
  styleUrls: ['./detail-tab.component.css'],
})
export class DetailTabComponent implements OnInit, OnDestroy {
  private readonly notificationDurationByType: Record<'success' | 'error' | 'warn' | 'info', number> = {
    success: 3000,
    error: 5000,
    warn: 5000,
    info: 3000,
  };

  @Input() classEntity!: ClassesEntity;
  @Input() classId!: string;
  @Input() userId!: string;
  @Input() userRole!: string;
  @Input() userName!: string;
  @Input() hasVmResource = false;
  @Input() hasAccResource = false;
  /**
   * Set by the shell once it has confirmed the VM template for this class
   * is fully provisioned. Controls Launch Lab visibility for trainer roles.
   */
  @Input() isVmTemplateReady = false;
  /** True when a snapshot already exists — hides the "Testing Done" card. */
  @Input() hasSnapshot = false;

  isLaunching = false;
  isCreatingSnapshot = false;

  /** Participant VM provisioning state (students only). null = not yet loaded. */
  vmProvisionState: UserVmProvisionState = null;
  /** True while the initial GET /user-vm/status call is in-flight. */
  vmStatusLoading = false;
  isProvisioningTrigger = false;

  /** Active resource tab: 'vm' or 'acc'. Defaults to whichever resource exists. */
  activeResourceTab: 'vm' | 'acc' = 'vm';

  // ── ACC credential state ──────────────────────────────────────────────────
  accCredState: AccCredentialState = null;
  accCredentials: { username: string; temporaryAccessPass: string; password: string } | null = null;
  /** participantId resolved from training key — needed for regenerate TAP. */
  private accParticipantId: string | null = null;
  isRegeneratingTap = false;
  accErrorMessage: string | null = null;

  private readonly stopPolling$ = new Subject<void>();
  private readonly stopAccPolling$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  /** Maximum number of 5-second polling ticks before the poll is abandoned (≈ 10 minutes). */
  private readonly MAX_POLL_TICKS = 120;

  // ── VM template metadata (fetched from /v1/resources/{policyTemplateId}) ──
  /** Full org-template resource model — null until loaded. */
  vmTemplateData: any | null = null;

  readonly resourceTypeLabels: Record<string, string> = {
    vm: 'Virtual Machine',
    acc: 'Cloud Account',
  };

  private readonly logger = Log.create(DetailTabComponent);

  /** True when the logged-in user has a trainer / instructor / faculty role. */
  get isTrainerRole(): boolean {
    return ['trainer', 'instructor', 'faculty'].includes(this.userRole?.toLowerCase());
  }

  /** True when the logged-in user is a student. */
  get isStudent(): boolean {
    return this.userRole?.toLowerCase() === 'student';
  }

  /**
   * Whether "Launch Lab" should be visible.
   * - Students  : visible when class is OnGoing.
   * - Trainers  : visible only when class is OnGoing AND the VM template is provisioned.
   */
  get showLaunchLab(): boolean {
    if (!this.hasVmResource) return false;
    const ongoing = this.classEntity?.status === 'OnGoing';
    if (!ongoing) return false;
    return this.isTrainerRole ? this.isVmTemplateReady : true;
  }

  /** "Testing Done" card: trainer-only, template must be ready, no snapshot yet. */
  get showTestingDone(): boolean {
    return this.hasVmResource && this.isTrainerRole && this.isVmTemplateReady && !this.hasSnapshot;
  }

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly unifiedLauncher: UnifiedVmLauncherService,
    private readonly snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
    private readonly confirmDialog: ConfirmDialogService,
    private readonly resourceService: ResourceCreatorService,
    private readonly masterData: ResourceMasterDataService,
  ) { }

  ngOnInit(): void {
    // Default the active resource tab to whichever resource exists (VM takes priority).
    this.activeResourceTab = this.hasVmResource ? 'vm' : 'acc';

    if (this.hasVmResource) {
      this.loadVmTemplateData();
    }
    if (this.isStudent && this.hasVmResource && this.classEntity?.status === 'OnGoing') {
      this.loadStatus();
    }
    if (this.isStudent && this.hasAccResource && this.classEntity?.status === 'OnGoing') {
      this.checkAccCredentialStatus();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
    this.stopAccPolling$.next();
    this.stopAccPolling$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get scheduleStartDate(): string {
    const ts = this.classEntity?.trainingSchedule as any;
    const d = ts?.startDate ?? ts?.dateRanges?.[0]?.startDate;
    if (!d) return '—';
    const date = new Date(d);
    return isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
  }

  get scheduleEndDate(): string {
    const ts = this.classEntity?.trainingSchedule as any;
    const d = ts?.endDate ?? ts?.dateRanges?.[0]?.endDate;
    if (!d) return '—';
    const date = new Date(d);
    return isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
  }

  get scheduleTime(): string {
    const s = this.classEntity?.trainingSchedule?.startTime;
    const e = this.classEntity?.trainingSchedule?.endTime;
    if (!s && !e) return '—';
    return `${s || '?'} – ${e || '?'}`;
  }

  get trainerNames(): string {
    const trainers = this.classEntity?.trainingSchedule?.trainers ?? [];
    if (!trainers.length) return 'No trainers assigned';
    return trainers.map((t: any) => t.name ?? t.email ?? t.id).join(', ');
  }

  get participantCount(): number {
    return this.classEntity?.trainingSchedule?.participants?.length ?? 0;
  }

  get resourceTypes(): string {
    const ids: any[] = this.classEntity?.trainingSchedule?.resourceTemplateIds ?? [];
    const types = ids.map((r: any) => r?.resourceType ?? r?.type ?? '').filter(Boolean);
    return types.map(t => this.resourceTypeLabels[t] ?? t).join(', ') || '—';
  }

  launchLab(): void {
    const preOpenedWindow = window.open('', `vm_${Date.now()}`);
    const isStudent = this.userRole?.toLowerCase() === 'student';

    this.isLaunching = true;

    const launchPromise = isStudent
      ? this.unifiedLauncher.launchParticipantSelfVm({
        userId: this.userId,
        classId: this.classId,
        userName: this.userName,
        vmConnectionTemplateId: this.getClassVmConnectionTemplateId(),
      }, preOpenedWindow)
      : this.launchClassTemplateVm(preOpenedWindow);

    launchPromise
      .catch((err: any) => {
        this.logger.e('Launch lab error:', err);
        this.notify(err?.error?.message ?? err?.message ?? 'Failed to launch VM', 'error');
        preOpenedWindow?.close();
      })
      .finally(() => {
        this.isLaunching = false;
      });
  }

  private async launchClassTemplateVm(preOpenedWindow?: Window | null): Promise<void> {
    const templateId = this.getClassVmTemplateId();
    if (!templateId) {
      this.notify('VM template ID not found for this class', 'error');
      preOpenedWindow?.close();
      return;
    }

    await this.unifiedLauncher.launchClassTemplateVm({
      userId: this.userId,
      templateId,
      classId: this.classId,
      userName: this.userName,
      vmConnectionTemplateId: this.getClassVmConnectionTemplateId(),
      uaTemplateId: this.getClassAccTemplateId(),
    }, preOpenedWindow);
  }

  private getClassVmTemplateId(): string | null {
    const templates: any[] = this.classEntity?.trainingSchedule?.resourceTemplateIds ?? [];
    const vmTemplate = templates.find((r: any) =>
      (r?.resourceType ?? r?.type ?? '').toLowerCase() === 'vm'
    );
    return vmTemplate?.id ?? vmTemplate?.templateId ?? vmTemplate?.Id ?? null;
  }

  /**
   * Returns the VM Connection Template id attached to this class, falling back to the legacy
   * nested data bag when the typed top-level field is absent (older class documents written
   * before the typed projection was added).
   */
  private getClassVmConnectionTemplateId(): string | null {
    return (this.classEntity as any)?.vmConnectionTemplateId
      ?? (this.classEntity as any)?.data?.['VmConnectionTemplateId']
      ?? null;
  }

  private getClassAccTemplateId(): string | null {
    const templates: any[] = this.classEntity?.trainingSchedule?.resourceTemplateIds ?? [];
    const tpl = templates.find(
      (t: any) => (t?.resourceType ?? t?.type ?? '').toLowerCase() === 'acc',
    );
    return tpl?.id ?? tpl?.Id ?? null;
  }

  copyToClipboard(value: string): void {
    navigator.clipboard?.writeText(value).then(() => {
      this.notify('Copied to clipboard', 'info');
    });
  }

  // ── ACC credential state machine ──────────────────────────────────────────

  /**
   * Read-only status check fired on page load.
   * Calls the backend with autoProvision=false so that simply opening the page
   * never triggers unintended provisioning.
   *
   * - 200 OK  → show credentials immediately (accCredState = 'active')
   * - 404 "Not provisioned" → show "Get Credentials" button (accCredState = null)
   * - 404 "in progress"     → start polling (accCredState = 'creating')
   * - 404 "failed"          → show error (accCredState = 'failed')
   */
  private checkAccCredentialStatus(): void {
    this.accCredState = 'fetching';
    this.callGetCredentials(false);
  }

  /**
   * Manual trigger from the "Get Credentials" button.
   * Passes autoProvision=true so the backend fires on-demand provisioning when the
   * account has not been started yet.
   */
  triggerGetCredentials(): void {
    this.stopAccPolling$.next();
    this.accCredState = 'fetching';
    this.callGetCredentials(true);
  }

  retryAccCredentials(): void {
    this.accErrorMessage = null;
    this.triggerGetCredentials();
  }

  /**
   * Performs the HTTP call and drives the state machine transition.
   *
   * @param autoProvision When true, the backend will trigger on-demand provisioning for
   *   NotStarted / absent accounts. Pass false for read-only status checks.
   *
   * Response handling:
   * - 200 with credentials   → active (credentials stored, polling stopped)
   * - 202 `{ state: "creating" }`   → creating (start/continue polling)
   * - 202 `{ state: "initiating" }` → initiating (start/continue polling)
   * - 404 "not provisioned"  → null (no polling — user must click "Get Credentials")
   * - 404 "failed"           → failed (stop polling, show error)
   * - other error            → failed
   */
  private callGetCredentials(autoProvision: boolean): void {
    const obs$ = this.isStudent
      ? this.classService.getCloudUserAccount(this.classId, this.userId, autoProvision)
      : this.classService.getCloudUserAccountForTemplate(this.classId);

    obs$.pipe(first(), takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        // 202 Accepted: provisioning is in flight.
        // Backend returns { state: 'creating' | 'initiating' } — start/continue polling.
        if (res?.state === 'creating' || res?.state === 'initiating') {
          const wasAlreadyPolling =
            this.accCredState === 'creating' || this.accCredState === 'initiating';
          this.accCredState = res.state;
          if (!wasAlreadyPolling) {
            this.startAccPolling();
          }
          return;
        }
        // 200 OK: credentials are ready.
        this.accCredentials = {
          username: res?.username ?? res?.UserName ?? '',
          temporaryAccessPass: res?.temporaryAccessPass ?? res?.TemporaryAccessPass ?? '',
          password: res?.password ?? res?.Password ?? '',
        };
        // Resolve participantId for regen-TAP (student flow resolves from training key on backend).
        // The UPN prefix is used as the participantId key.
        this.accParticipantId = res?.participantId ?? null;
        this.accCredState = 'active';
        this.stopAccPolling$.next();
      },
      error: (err: any) => {
        const msg: string = err?.error ?? err?.message ?? '';
        if (typeof msg === 'string' && msg.toLowerCase().includes('not provisioned')) {
          // Status-check found no account — surface the "Get Credentials" button without polling.
          this.accCredState = null;
          this.stopAccPolling$.next();
        } else if (typeof msg === 'string' && msg.toLowerCase().includes('failed')) {
          this.accCredState = 'failed';
          this.accErrorMessage = 'Provisioning failed — an administrator must retry via settings.';
          this.stopAccPolling$.next();
        } else {
          // Unexpected HTTP error — stop polling and surface an actionable message.
          this.accCredState = 'failed';
          this.accErrorMessage = 'Failed to retrieve credentials — please refresh the page.';
          this.stopAccPolling$.next();
        }
      },
    });
  }

  /**
   * Polls GET credentials every 5 s until the account becomes active or failed.
   * Automatically stops after {@link MAX_POLL_TICKS} ticks (≈ 10 minutes) to prevent
   * an unbounded loop if the backend stalls.
   *
   * Implementation note: the timeout is enforced inside the `next` callback via a tick
   * counter rather than via RxJS `take() + complete`.  Using `take + complete` would
   * trigger the `complete` handler whenever `stopAccPolling$.next()` is called (e.g. to
   * stop the previous loop before starting a new one), firing a false timeout while the
   * account is still actively being provisioned.
   */
  private startAccPolling(): void {
    // Stop any pre-existing poll before starting a new one.
    this.stopAccPolling$.next();

    let tickCount = 0;

    interval(5000).pipe(
      takeUntil(this.stopAccPolling$),
      takeUntil(this.destroy$),
    ).subscribe({
      next: () => {
        tickCount++;
        if (tickCount > this.MAX_POLL_TICKS) {
          if (this.accCredState !== 'active' && this.accCredState !== 'failed') {
            this.accCredState = 'failed';
            this.accErrorMessage =
              'Provisioning is taking longer than expected — ' +
              'refresh the page or contact your trainer.';
          }
          this.stopAccPolling$.next();
          return;
        }
        this.callGetCredentials(false);
      },
    });
  }

  /**
   * Regenerates the TAP for the participant's cloud user account.
   * Shows a confirmation dialog first; on confirm POSTs to the backend.
   * The participantId is extracted from the UPN stored in accCredentials if not resolved yet.
   */
  regenerateTap(): void {
    // Derive participantId: prefer the one returned by the backend, fall back to username prefix.
    const participantId = this.accParticipantId
      ?? this.accCredentials?.username?.split('@')[0]
      ?? null;

    if (!participantId) {
      this.notify('Cannot determine participant ID for TAP regeneration', 'error');
      return;
    }

    this.confirmDialog.confirm({
      title: 'Regenerate Temporary Access Pass',
      message: 'This will invalidate the current TAP and generate a new one. The new TAP will be displayed immediately.',
      confirmText: 'Regenerate TAP',
      cancelText: 'Cancel',
    }).subscribe(confirmed => {
      if (!confirmed) return;

      this.isRegeneratingTap = true;
      this.classService.regenerateParticipantTap(this.classId, participantId)
        .pipe(first(), takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            if (this.accCredentials) {
              this.accCredentials = { ...this.accCredentials, temporaryAccessPass: res.temporaryAccessPass };
            }
            this.isRegeneratingTap = false;
            this.notify('Temporary Access Pass regenerated successfully', 'success');
          },
          error: (err: any) => {
            this.isRegeneratingTap = false;
            this.logger.e('Regenerate TAP error:', err);
            this.notify(err?.error?.message ?? 'Failed to regenerate TAP', 'error');
          },
        });
    });
  }

  openAzurePortal(): void {
    window.open('https://portal.azure.com', '_blank', 'noopener,noreferrer');
  }

  /**
   * Creates a snapshot of the class VM template — marks "Testing Done".
   * Only visible for trainer roles when a template is provisioned and no snapshot exists.
   */
  markTestingDone(): void {
    const templates: any[] = this.classEntity?.trainingSchedule?.resourceTemplateIds ?? [];
    const vmTemplate = templates.find(r => (r?.resourceType ?? r?.type ?? '').toLowerCase() === 'vm');
    const templateId = vmTemplate?.id ?? vmTemplate?.templateId ?? vmTemplate?.Id;

    if (!templateId) {
      this.notify('VM template ID not found for this class', 'error');
      return;
    }

    this.isCreatingSnapshot = true;
    this.classService.createSnapshot(templateId, this.classId).subscribe({
      next: () => {
        this.isCreatingSnapshot = false;
        this.notify('Testing marked as done — snapshot created successfully.', 'success');
        // Optimistically hide the card; the parent will confirm on next load.
        this.hasSnapshot = true;
      },
      error: (err: any) => {
        this.isCreatingSnapshot = false;
        this.logger.e('Snapshot creation failed:', err);
        this.notify(err?.error?.message ?? 'Failed to create snapshot.', 'error');
      },
    });
  }

  private notify(msg: string, type: 'success' | 'error' | 'warn' | 'info'): void {
    this.snackBar.open(msg, 'Dismiss', {
      duration: this.notificationDurationByType[type],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  // ── VM template metadata helpers ─────────────────────────────────────────

  /**
   * Fetches the org-template resource model (via resourceId) which holds
   * CloudProviderData and SoftwareData needed for the meta card.
   */
  private loadVmTemplateData(): void {
    const templates: any[] = this.classEntity?.trainingSchedule?.resourceTemplateIds ?? [];
    const vmEntry = templates.find((r: any) =>
      (r?.resourceType ?? r?.type ?? r?.ResourceType ?? '').toLowerCase() === 'vm'
    );
    const resourceId = vmEntry?.ResourceId ?? vmEntry?.resourceId;
    if (!resourceId) return;

    this.resourceService.getResourceMetadata(resourceId)
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe({
        next: (model: any) => { this.vmTemplateData = model; },
        error: () => { /* non-critical — metadata card simply stays minimal */ },
      });
  }

  get vmTemplateName(): string {
    const templates: any[] = this.classEntity?.trainingSchedule?.resourceTemplateIds ?? [];
    const vmEntry = templates.find((r: any) =>
      (r?.resourceType ?? r?.type ?? r?.ResourceType ?? '').toLowerCase() === 'vm'
    );
    return this.vmTemplateData?.metaData?.name ?? vmEntry?.Name ?? vmEntry?.name ?? '—';
  }

  get vmTemplateDescription(): string {
    return this.vmTemplateData?.data?.['TemplateDetailsData']?.templateDescription
      ?? this.vmTemplateData?.metaData?.description ?? '';
  }

  get vmCloudProvider(): string {
    const provider: string = this.vmTemplateData?.data?.['CloudProviderData']?.provider ?? '';
    if (!provider) return '';
    return this.masterData.get<string>(`cloud.${provider}.name`) ?? provider;
  }

  get vmRegion(): string {
    const cd = this.vmTemplateData?.data?.['CloudProviderData'];
    if (!cd?.provider || !cd?.region) return '';
    const regions = this.masterData.get<{ id: string; name: string }[]>(`cloud.${cd.provider}.regions`) ?? [];
    return regions.find((r: any) => r.id === cd.region)?.name ?? cd.region;
  }

  get vmSize(): string {
    const cd = this.vmTemplateData?.data?.['CloudProviderData'];
    if (!cd?.provider || !cd?.size) return '';
    const sizes = this.masterData.get<{ id: string; name: string }[]>(`cloud.${cd.provider}.sizes`) ?? [];
    return sizes.find((s: any) => s.id === cd.size)?.name ?? cd.size;
  }

  get vmOs(): string {
    const sw = this.vmTemplateData?.data?.['SoftwareData'];
    if (!sw?.os || !sw?.osVersion) return '';
    const versions = this.masterData.get<{ id: string; name: string }[]>(`software.os.${sw.os}.versions`) ?? [];
    return versions.find((v: any) => v.id === sw.osVersion)?.name ?? sw.osVersion;
  }

  get vmSdkNames(): string[] {
    const ids: string[] = this.vmTemplateData?.data?.['SoftwareData']?.sdks ?? [];
    const all = this.masterData.get<{ id: string; name: string }[]>('software.sdks') ?? [];
    return ids.map(id => all.find((s: any) => s.id === id)?.name ?? id);
  }

  get vmEditorNames(): string[] {
    const ids: string[] = this.vmTemplateData?.data?.['SoftwareData']?.editors ?? [];
    const all = this.masterData.get<{ id: string; name: string }[]>('software.editors') ?? [];
    return ids.map(id => all.find((e: any) => e.id === id)?.name ?? id);
  }

  get vmDatabaseNames(): string[] {
    const ids: string[] = this.vmTemplateData?.data?.['SoftwareData']?.databases ?? [];
    const all = this.masterData.get<{ id: string; name: string }[]>('software.databases') ?? [];
    return ids.map(id => all.find((d: any) => d.id === id)?.name ?? id);
  }

  get vmMiscNames(): string[] {
    const ids: string[] = this.vmTemplateData?.data?.['SoftwareData']?.misc ?? [];
    const all = this.masterData.get<{ id: string; name: string }[]>('software.misc') ?? [];
    return ids.map(id => all.find((m: any) => m.id === id)?.name ?? id);
  }

  get vmHasSoftware(): boolean {
    return this.vmSdkNames.length > 0
      || this.vmEditorNames.length > 0
      || this.vmDatabaseNames.length > 0
      || this.vmMiscNames.length > 0;
  }

  /**
   * Builds the {@link TemplateInfoCardData} for the shared template-info-card.
   * Installation summary and status chip are intentionally omitted — not relevant
   * in the student view where VM provisioning state is shown separately.
   */
  get templateInfoCardData(): TemplateInfoCardData {
    return {
      name: this.vmTemplateName,
      cardSubtitle: this.vmTemplateDescription || undefined,
      provider: this.vmCloudProvider,
      region: this.vmRegion,
      vmSize: this.vmSize,
      osLabel: this.vmOs,
      sdks: this.vmSdkNames,
      editors: this.vmEditorNames,
      tools: this.vmMiscNames,
      databases: this.vmDatabaseNames,
      isLoading: this.vmTemplateData === null,
    };
  }

  // ── Student VM polling state machine ────────────────────────────────────

  /**
   * Loads the current VM status on page open.
   * If the VM is already in a terminal state the polling loop is not started.
   */
  private loadStatus(): void {
    this.vmStatusLoading = true;
    this.classService.getUserVmStatus(this.classId).pipe(
      first(),
      takeUntil(this.destroy$)
    ).subscribe({
      next: res => {
        this.vmProvisionState = res.state;
        this.vmStatusLoading = false;
        if (res.state === 'creating') {
          this.startPolling();
        }
      },
      error: err => {
        this.logger.e('[StudentVm] loadStatus error:', err);
        this.vmStatusLoading = false;
      },
    });
  }

  /**
   * Polls GET /user-vm/status every 5 seconds.
   * Stops automatically when a terminal state (ready | failed) is reached.
   */
  private startPolling(): void {
    const TERMINAL: UserVmProvisionState[] = ['ready', 'failed'];
    interval(5000).pipe(
      takeUntil(this.stopPolling$),
      takeUntil(this.destroy$),
      switchMap(() => this.classService.getUserVmStatus(this.classId))
    ).subscribe({
      next: res => {
        this.vmProvisionState = res.state;
        if (TERMINAL.includes(res.state)) {
          this.stopPolling$.next();
        }
      },
      error: err => {
        this.logger.e('[StudentVm] poll error:', err);
        this.stopPolling$.next();
      },
    });
  }

  /**
   * Called by the student when they click "Prepare VM".
   * Calls POST /user-vm/prepare, then starts polling.
   */
  triggerProvision(): void {
    this.isProvisioningTrigger = true;
    this.classService.prepareUserVm(this.classId).pipe(
      first(),
      takeUntil(this.destroy$)
    ).subscribe({
      next: res => {
        this.isProvisioningTrigger = false;
        this.vmProvisionState = res.state;
        if (res.state === 'creating') {
          this.startPolling();
        }
      },
      error: err => {
        this.isProvisioningTrigger = false;
        this.logger.e('[StudentVm] prepareUserVm error:', err);
        this.notify(err?.error?.message ?? 'Failed to start VM provisioning', 'error');
      },
    });
  }

  /**
   * Launches the Guacamole session once the VM is ready.
   * Called by the "Open VM" button in the student ready-state card.
   */
  openVmSession(): void {
    const preOpenedWindow = window.open('', `vm_${Date.now()}`);
    this.isLaunching = true;
    this.unifiedLauncher.launchParticipantSelfVm({
      userId: this.userId,
      classId: this.classId,
      userName: this.userName,
      vmConnectionTemplateId: this.getClassVmConnectionTemplateId(),
    }, preOpenedWindow)
      .catch((err: any) => {
        this.logger.e('[StudentVm] openVmSession error:', err);
        this.notify(err?.error?.message ?? err?.message ?? 'Failed to open VM session', 'error');
        preOpenedWindow?.close();
      })
      .finally(() => {
        this.isLaunching = false;
      });
  }
}
